import { motion } from "framer-motion";
import { ShieldCheck, Database, Zap } from "lucide-react";

import Container from "../../Container/Container";
import { fadeLeft, fadeRight, viewportOnce } from "../../../Utils/animations";
import { dataQuality } from "../../../Data/ceoData";

import "../../../Style/home/ceo.css";

function DataQualitySection() {
  return (
    <section className="section section--deep-tint ceo">
      <Container className="ceo__grid">
        {/* Left Column: Data Quality Messaging & Metrics */}
        <motion.div
          className="ceo__content"
          variants={fadeLeft()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <span className="section-eyebrow">
            <span className="section-eyebrow__dot" aria-hidden="true" />
            {dataQuality.eyebrow}
          </span>

          <h2 className="section-title mt-4">
            {dataQuality.title}{" "}
            <span className="text-gradient">{dataQuality.highlight}</span>
          </h2>

          <p className="section-description mt-4">{dataQuality.description}</p>
          <p className="ceo__vision">{dataQuality.quote}</p>

          <div className="quality__metrics-row">
            {dataQuality.stats.map((stat) => (
              <div key={stat.label} className="quality__metric-item">
                <strong className="quality__metric-val">{stat.value}</strong>
                <span className="quality__metric-lbl">{stat.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right Column: Animated Interactive Data Verification Scanner */}
        <motion.div
          className="ceo__portrait"
          variants={fadeRight(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <div className="quality__visual-wrapper">
            {/* Animated SVG Radar Scanner */}
            <svg
              className="quality__scanner-svg"
              viewBox="0 0 340 340"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient
                  id="scanBeam"
                  x1="170"
                  y1="170"
                  x2="310"
                  y2="71"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.45" />
                  <stop offset="60%" stopColor="var(--secondary)" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="var(--secondary)" stopOpacity="0" />
                </linearGradient>
                <radialGradient id="centerGlow">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* Glowing Background Core */}
              <circle cx="170" cy="170" r="140" fill="url(#centerGlow)" />

              {/* Concentric Grid Rings */}
              <circle
                cx="170"
                cy="170"
                r="140"
                stroke="var(--border-color)"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              <circle
                cx="170"
                cy="170"
                r="100"
                stroke="var(--border-strong)"
                strokeWidth="1.2"
              />
              <circle
                cx="170"
                cy="170"
                r="60"
                stroke="var(--border-color)"
                strokeWidth="1"
                strokeDasharray="2 4"
              />
              <circle
                cx="170"
                cy="170"
                r="20"
                stroke="var(--secondary)"
                strokeWidth="1.5"
              />

              {/* Crosshairs */}
              <line
                x1="170"
                y1="20"
                x2="170"
                y2="320"
                stroke="var(--border-subtle)"
                strokeWidth="1"
              />
              <line
                x1="20"
                y1="170"
                x2="320"
                y2="170"
                stroke="var(--border-subtle)"
                strokeWidth="1"
              />

              {/* Native Hardware-Accelerated SVG Rotation (Center Locked to 170, 170) */}
              <g>
                <path
                  d="M 170 170 L 310 170 A 140 140 0 0 0 269 71 Z"
                  fill="url(#scanBeam)"
                />
                <line
                  x1="170"
                  y1="170"
                  x2="310"
                  y2="170"
                  stroke="var(--secondary)"
                  strokeWidth="1.5"
                  strokeOpacity="0.8"
                />
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 170 170"
                  to="360 170 170"
                  dur="4.5s"
                  repeatCount="indefinite"
                />
              </g>

              {/* Pulsing Concentric Radar Wave */}
              <motion.circle
                cx="170"
                cy="170"
                stroke="var(--secondary)"
                strokeWidth="1.5"
                initial={{ r: 20, opacity: 0.8 }}
                animate={{ r: 145, opacity: 0 }}
                transition={{ repeat: Infinity, duration: 2.6, ease: "easeOut" }}
              />

              {/* Ping Nodes (Verified data points blinking on the map) */}
              <motion.circle
                cx="230"
                cy="110"
                r="4.5"
                fill="var(--secondary)"
                animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
                transition={{ repeat: Infinity, duration: 2, delay: 0.2 }}
              />
              <motion.circle
                cx="110"
                cy="220"
                r="4"
                fill="var(--primary)"
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ repeat: Infinity, duration: 2.2, delay: 0.8 }}
              />
              <motion.circle
                cx="120"
                cy="120"
                r="3.5"
                fill="#10b981"
                animate={{ scale: [1, 1.6, 1], opacity: [0.4, 1, 0.4] }}
                transition={{ repeat: Infinity, duration: 1.8, delay: 0.5 }}
              />
            </svg>

            {/* Central Icon Hub */}
            <div className="quality__hub">
              <Database size={24} className="quality__hub-icon" />
            </div>

            {/* Floating Live Badge 1: Clean Status */}
            <motion.div
              className="quality__badge quality__badge--top"
              animate={{ y: [-4, 4, -4] }}
              transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
            >
              <ShieldCheck size={16} className="text-emerald-400" />
              <div>
                <strong>Valid & Active</strong>
                <span>SMTP Verified</span>
              </div>
            </motion.div>

            {/* Floating Live Badge 2: Real-time scrubbing */}
            <motion.div
              className="quality__badge quality__badge--bottom"
              animate={{ y: [4, -4, 4] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            >
              <Zap size={16} className="text-amber-400" />
              <div>
                <strong>De-duplication</strong>
                <span>Instant scrub active</span>
              </div>
            </motion.div>
          </div>

          <span className="blob blob--magenta ceo__blob" />
        </motion.div>
      </Container>
    </section>
  );
}

export default DataQualitySection;